import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, KeyRound } from 'lucide-react';

export const DualLoginCard = ({ className = '' }) => {
  const { refresh } = useAuth();
  const [pid, setPid] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotPid, setForgotPid] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pid || !pw) { toast.error('Enter your ID and password'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/auth/password/login', { personal_id: pid.trim().toUpperCase(), password: pw });
      if (data?.session_token) localStorage.setItem('session_token', data.session_token);
      await refresh?.();
      toast.success(`Welcome, ${data?.user?.name || data?.user?.personal_id}`);
      window.location.href = '/store';
    } catch (e2) {
      const msg = e2.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg.replace(/_/g, ' ') : 'Login failed');
    } finally { setBusy(false); }
  };

  const submitForgot = async () => {
    if (!forgotPid) return;
    try {
      await api.post('/auth/password/forgot', { personal_id: forgotPid.trim().toUpperCase() });
      toast.success('If that ID exists, a reset link has been generated for admin to relay.');
      setForgotOpen(false); setForgotPid('');
    } catch { toast.error('Could not submit request'); }
  };

  return (
    <div className={`bg-white/95 backdrop-blur-md p-6 sm:p-7 rounded-[1.75rem] shadow-2xl border border-white ${className}`} data-testid="dual-login-card">
      <div className="mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1D4ED8]">Welcome back</div>
        <h3 className="text-xl font-black display text-[#1E3A8A] mt-0.5">Access your account</h3>
      </div>
      <div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Personal ID</Label>
              <Input value={pid} onChange={e => setPid(e.target.value.toUpperCase())} placeholder="e.g. IL10001" className="font-mono-bz rounded-xl h-11 mt-1" data-testid="login-pid-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Password</Label>
              <Input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Your password" className="rounded-xl h-11 mt-1" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#1D4ED8] hover:bg-[#1E3A8A] font-bold display gap-2 active:scale-95 transition-transform" data-testid="login-submit-btn">
              <KeyRound className="h-4 w-4" /> {busy ? 'Signing in…' : 'Sign in'} <ArrowRight className="h-4 w-4" />
            </Button>
            <button type="button" onClick={() => setForgotOpen(v => !v)} className="text-xs text-[#1D4ED8] font-semibold hover:underline" data-testid="forgot-pw-btn">Forgot password?</button>
          </form>
          {forgotOpen && (
            <div className="mt-3 p-3 rounded-xl bg-slate-50 space-y-2" data-testid="forgot-pw-panel">
              <Label className="text-xs">Enter your Personal ID</Label>
              <Input value={forgotPid} onChange={e => setForgotPid(e.target.value.toUpperCase())} placeholder="IL10001" className="font-mono-bz rounded-xl h-10" data-testid="forgot-pid-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitForgot} className="rounded-full bg-[#1D4ED8]" data-testid="submit-forgot-btn">Send reset link</Button>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setForgotOpen(false)}>Cancel</Button>
              </div>
              <p className="text-[10px] text-[var(--bz-muted)]">An admin will relay your reset link via WhatsApp.</p>
            </div>
          )}
      </div>
    </div>
  );
};

export default DualLoginCard;
